import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { connect, FormikProps } from 'formik';
import { compose } from 'recompose';
import { findIndex } from 'lodash';

import { Content, ContentUrlParamOptions } from 'api';

import URLTrackingOption from './URLTrackingOption';

interface IProps {
  contentUrlParamOptions: ContentUrlParamOptions[];
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles> {
  formik: FormikProps<Content>;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(3),
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(connect, withStyles(styles));

const URLTracking: FC<IPropsEnhanced> = ({ classes, formik, contentUrlParamOptions }) => {
  const { t } = useTranslation();
  const { url_param_values } = formik.values;

  useEffect(() => setParamOptions(), []);

  function getIndexForKey(key: string) {
    const index = findIndex(url_param_values, ['key', key]);
    return index >= 0 ? index : undefined;
  }

  function getKey(keyIndex: number) {
    return url_param_values[keyIndex] && url_param_values[keyIndex].key;
  }

  function getValue(keyIndex: number) {
    return url_param_values[keyIndex] && url_param_values[keyIndex].value;
  }

  function handleUrlParamOption(index: number, value: string) {
    formik.setFieldValue(`url_param_values[${index}].value`, value);
  }

  function setParamOptions() {
    contentUrlParamOptions.forEach((paramOptions, index) => {
      const { key, default_option_value, options } = paramOptions;
      const keyIndex = getIndexForKey(key) || index;
      const optionKey = getKey(keyIndex) || key;
      const optionValue = getValue(keyIndex) || default_option_value || options[0];

      url_param_values[keyIndex] = { key: optionKey, value: optionValue };
    });

    formik.setFieldValue('url_param_values', url_param_values);
  }

  return (
    <div className={classes.root}>
      <Typography variant="subtitle2">{t('posting:urlTracking')}</Typography>
      {contentUrlParamOptions.map((paramOptions, index) => (
        <URLTrackingOption
          key={paramOptions.key}
          index={getIndexForKey(paramOptions.key) || index}
          paramOptions={paramOptions}
          onChange={handleUrlParamOption}
        />
      ))}
    </div>
  );
};

URLTracking.defaultProps = {
  contentUrlParamOptions: [],
};

export default enhancers(URLTracking);
